import {
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useState,
  useRef,
} from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import getValidationMessage from 'helpers/getValidationMessage';

import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';

import {
  addMissingMandatoryField,
  removeMissingMandatoryField,
  addRemark,
  removeRemark,
  updateRemark,
  addFile,
  removeFile,
} from 'reducers/autoPersonalFormReducer';

import { getFieldId } from '../resolverUtils';

import { APIRemarkFile, APIRemark } from 'types/api.types';

export type VehiclePurchaseDateResolutionType = 'remarks' | 'file';

export interface UseVehiclePurchaseDateResolverConfig {
  validationId: string;
  section: number;
  itemKey: string;
  formStateKey: 'autoPersonalForm';
  fileType: number;
  fileLabel: string;
}

export interface VehiclePurchaseDateFieldIds {
  resolution: string;
  remarks: string;
}

export interface VehiclePurchaseDateResolverState {
  remark: APIRemark | undefined;
  file: APIRemarkFile | undefined;
  resolutionType: VehiclePurchaseDateResolutionType | null;
  show: boolean;
  showFileUpload: boolean;
  showRemarksField: boolean;
}

export interface VehiclePurchaseDateResolverHandlers {
  handleResolutionTypeChange: (value: string) => void;
  handleRemarksChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const useVehiclePurchaseDateResolver = (
  config: UseVehiclePurchaseDateResolverConfig,
) => {
  const { validationId, section, itemKey, formStateKey, fileType, fileLabel } =
    config;

  const { validations } = useContext(ApplicationFormContext);
  const dispatch = useAppDispatch();

  const mandatoryFileValidations = getMandatoryFileValidations(formStateKey);

  const alertMessage = getValidationMessage(validations, validationId);
  const show = !!alertMessage;

  const remark = useAppSelector((state) => {
    return state[formStateKey].remarks.find(
      (r: any) => r.validation_key === validationId && r.item_key === itemKey,
    );
  });

  const file = useAppSelector((state) => {
    return state[formStateKey].files.find(
      (f: any) => f.validation_key === validationId && f.item_key === itemKey,
    );
  });

  const getInitialResolutionType =
    (): VehiclePurchaseDateResolutionType | null => {
      if (remark) return 'remarks';
      if (file) return 'file';
      return null;
    };

  const [resolutionType, setResolutionType] =
    useState<VehiclePurchaseDateResolutionType | null>(
      getInitialResolutionType,
    );

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && (remark || file)) {
      const inferred = getInitialResolutionType();
      if (inferred) {
        setResolutionType(inferred);
      }
      hasInitialized.current = true;
    }
  }, [remark, file]);

  useEffect(() => {
    if (!show) {
      setResolutionType(null);
      hasInitialized.current = false;
    }
  }, [show]);

  const showFileUpload = resolutionType === 'file';
  const showRemarksField = resolutionType === 'remarks';

  const fieldIds: VehiclePurchaseDateFieldIds = useMemo(
    () => ({
      resolution: getFieldId('Vehicle Purchase Date Resolution', itemKey),
      remarks: getFieldId('Vehicle Purchase Date Remarks', itemKey),
    }),
    [itemKey],
  );

  const prevResolutionType = useRef<VehiclePurchaseDateResolutionType | null>(
    null,
  );

  useEffect(() => {
    if (!show) {
      dispatch(
        removeRemark({ validation_key: validationId, item_key: itemKey }),
      );
      dispatch(removeFile({ validation_key: validationId, item_key: itemKey }));
      prevResolutionType.current = null;
      return;
    }

    if (resolutionType === prevResolutionType.current) {
      return;
    }

    if (resolutionType === 'remarks') {
      dispatch(removeFile({ validation_key: validationId, item_key: itemKey }));
      if (!remark) {
        dispatch(
          addRemark({
            validation_key: validationId,
            item_key: itemKey,
            text: '',
            status: 'created',
            text_validator_log_id: null,
            ai_result: '',
            section,
          }),
        );
      }
    } else if (resolutionType === 'file') {
      dispatch(
        removeRemark({ validation_key: validationId, item_key: itemKey }),
      );
      dispatch(removeFile({ validation_key: validationId, item_key: itemKey }));
      dispatch(
        addFile({
          validation_key: validationId,
          item_key: itemKey,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section,
          type: fileType,
          files: [],
          text: fileLabel,
          mandatory: mandatoryFileValidations.includes(validationId),
        }),
      );
    } else if (resolutionType === null && prevResolutionType.current !== null) {
      dispatch(
        removeRemark({ validation_key: validationId, item_key: itemKey }),
      );
      dispatch(removeFile({ validation_key: validationId, item_key: itemKey }));
    }

    prevResolutionType.current = resolutionType;
  }, [
    show,
    resolutionType,
    fileType,
    fileLabel,
    validationId,
    itemKey,
    section,
    mandatoryFileValidations,
    dispatch,
    remark,
  ]);

  useEffect(() => {
    if (!show) {
      dispatch(removeMissingMandatoryField(fieldIds.resolution));
      dispatch(removeMissingMandatoryField(fieldIds.remarks));
      return;
    }

    if (!resolutionType) {
      dispatch(
        addMissingMandatoryField({ name: fieldIds.resolution, section }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.resolution));
    }

    if (
      showRemarksField &&
      remark &&
      (!remark.text ||
        remark.text.trim().length === 0 ||
        remark.status !== 'approved')
    ) {
      dispatch(addMissingMandatoryField({ name: fieldIds.remarks, section }));
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.remarks));
    }
  }, [
    show,
    remark,
    resolutionType,
    showRemarksField,
    section,
    fieldIds,
    dispatch,
  ]);

  const handleResolutionTypeChange = useCallback((value: string) => {
    setResolutionType(value as VehiclePurchaseDateResolutionType);
  }, []);

  const handleRemarksChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            text: e.target.value,
            status: 'created',
          },
        }),
      );
    },
    [dispatch, validationId, itemKey],
  );

  const getCompactAlertStatus = useCallback(() => {
    if (!resolutionType) return 'warning';

    if (resolutionType === 'remarks') {
      if (!remark || remark.status !== 'approved') return 'warning';
      return 'success';
    }

    if (resolutionType === 'file') {
      if (!file) return 'warning';
      if (file.status !== 'approved' && !file.is_deferred) return 'warning';
      return 'success';
    }

    return 'warning';
  }, [remark, file, resolutionType]);

  const state: VehiclePurchaseDateResolverState = {
    remark,
    file,
    resolutionType,
    show,
    showFileUpload,
    showRemarksField,
  };

  const handlers: VehiclePurchaseDateResolverHandlers = {
    handleResolutionTypeChange,
    handleRemarksChange,
  };

  return {
    state,
    handlers,
    fieldIds,
    alertMessage,
    getCompactAlertStatus,
  };
};
